import React from "react";
import { Link2, ExternalLink, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface CreativeDisplayProps {
  url: string;
  index: number;
  getResolutionBadgeColor?: (url: string) => string;
  getResolutionLabel?: (url: string) => string;
  showBadge?: boolean;
}

// Helper function to check if a URL is an external link (not a direct media file)
export const isExternalLink = (url: string): boolean => {
  // Check if URL ends with common media extensions
  const mediaExtensions = /\.(jpg|jpeg|png|gif|webp|svg|mp4|webm|mov|avi|mkv|bmp|tiff)$/i;
  
  // Check if it's a Supabase storage URL (contains 'supabase' and 'storage')
  const isSupabaseStorage = url.includes('supabase') && url.includes('storage');
  
  // If it's a media file or Supabase storage, it's NOT an external link
  if (mediaExtensions.test(url) || isSupabaseStorage) {
    return false;
  }
  
  // Common external link patterns (Google Drive, Dropbox, OneDrive, etc.)
  const externalPatterns = [
    /drive\.google\.com/i,
    /docs\.google\.com/i,
    /dropbox\.com/i,
    /onedrive\.live\.com/i,
    /1drv\.ms/i,
    /wetransfer\.com/i,
    /mega\.nz/i,
    /mediafire\.com/i,
    /box\.com/i,
    /icloud\.com/i,
  ];
  
  // Check if URL matches any external pattern
  if (externalPatterns.some(pattern => pattern.test(url))) {
    return true;
  }
  
  // If URL doesn't have a media extension and isn't Supabase, treat as external
  return !mediaExtensions.test(url);
};

// Helper to get a display name from URL
const getUrlDisplayName = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace('www.', '');
    
    // Special cases for known services
    if (hostname.includes('drive.google.com') || hostname.includes('docs.google.com')) {
      return 'Google Drive';
    }
    if (hostname.includes('dropbox.com')) {
      return 'Dropbox';
    }
    if (hostname.includes('onedrive') || hostname.includes('1drv.ms')) {
      return 'OneDrive';
    }
    if (hostname.includes('wetransfer.com')) {
      return 'WeTransfer';
    }
    
    return hostname;
  } catch {
    return 'External Link';
  }
};

const CreativeDisplay: React.FC<CreativeDisplayProps> = ({
  url,
  index,
  getResolutionBadgeColor,
  getResolutionLabel,
  showBadge = true
}) => {
  const isExternal = isExternalLink(url);
  const isVideo = url.match(/\.(mp4|webm|mov)$/i);

  if (isExternal) {
    return (
      <div className="relative group">
        <div className="aspect-video bg-muted rounded-lg overflow-hidden flex items-center justify-center border-2 border-dashed border-border">
          <div className="text-center p-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
              <Link2 className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">
              {getUrlDisplayName(url)}
            </p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline flex items-center justify-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <span>Open Link</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
        {showBadge && (
          <div className="mt-2">
            <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/20">
              External Link
            </Badge>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative group">
      <div className="aspect-video bg-muted rounded-lg overflow-hidden">
        {isVideo ? (
          <video 
            src={url} 
            className="w-full h-full object-cover"
            controls
          />
        ) : (
          <img 
            src={url} 
            alt={`Creative ${index + 1}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder.svg';
            }}
          />
        )}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white hover:text-primary-foreground"
          >
            <Eye className="h-6 w-6" />
          </a>
        </div>
      </div>
      {showBadge && getResolutionBadgeColor && getResolutionLabel && (
        <div className="mt-2">
          <Badge 
            variant="outline" 
            className={`text-xs ${getResolutionBadgeColor(url)}`}
          >
            {getResolutionLabel(url)}
          </Badge>
        </div>
      )}
    </div>
  );
};

export default CreativeDisplay;
